import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import {
  createAutopilotRule,
  deleteAutopilotRule,
  fetchAutopilotAudit,
  fetchAutopilotRules,
  updateAutopilotRule,
} from '@/api/autopilot';
import { fetchReasons } from '@/api/reasons';
import { fetchSettings } from '@/api/settings';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { KpiCard } from '@/components/ui/KpiCard';
import { TextField } from '@/components/ui/TextField';
import { SelectField } from '@/components/ui/SelectField';
import { Toggle } from '@/components/ui/Toggle';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { GripIcon, TrashIcon, LightningIcon, FilterIcon, CheckIcon } from '@/components/icons';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import {
  AUTOPILOT_ACTION_TYPES,
  AUTOPILOT_CONDITION_FIELDS,
  AUTOPILOT_OPERATORS,
  AUTOPILOT_TRIGGER_EVENTS,
  refundModeLabel,
  resolveReturnReasons,
  resolveSettings,
} from '@/constants/defaults';
import { formatDate, formatDateTime, humanizeEnum } from '@/lib/format';
import { errorMessageOf } from '@/lib/errors';
import { cn } from '@/lib/cn';
import type {
  AutopilotAction,
  AutopilotActionType,
  AutopilotAuditEntry,
  AutopilotCondition,
  AutopilotOperator,
  AutopilotRule,
  AutopilotRuleInput,
  MerchantSettings,
  Page,
  ReturnReason,
} from '@/types';

interface SelectOption {
  value: string;
  label: string;
}

const NUMERIC_CONDITION_FIELDS = new Set(['itemSubtotal', 'netRefund']);
const POSITIVE_INT_CONDITION_FIELDS = new Set(['itemCount']);
const CATEGORICAL_CONDITION_FIELDS = new Set(['refundMode', 'reasonSlug']);
const PINCODE_CONDITION_FIELDS = new Set(['pincode']);

/** Pincode list inputs: digits and separating commas only. */
function sanitizePincodeListInput(raw: string): string {
  return raw.replace(/[^0-9,]/g, '');
}

/** Amount inputs: digits + a single decimal point, no negatives. */
function sanitizeAmountInput(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) {
    return cleaned;
  }
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}

/** Count inputs: digits only, minimum 1. */
function sanitizeCountInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') {
    return '';
  }
  return String(Math.max(1, parseInt(digits, 10)));
}

function defaultValueForField(field: string, refundModes: SelectOption[], reasons: SelectOption[]): string {
  if (field === 'refundMode') {
    return refundModes[0]?.value ?? '';
  }
  if (field === 'reasonSlug') {
    return reasons[0]?.value ?? '';
  }
  if (POSITIVE_INT_CONDITION_FIELDS.has(field)) {
    return '1';
  }
  if (NUMERIC_CONDITION_FIELDS.has(field)) {
    return '0';
  }
  return '';
}

function operatorsForField(field: string): { value: AutopilotOperator; label: string }[] {
  if (PINCODE_CONDITION_FIELDS.has(field)) {
    return AUTOPILOT_OPERATORS.filter((operator) => operator.value === 'in' || operator.value === 'not_in');
  }
  if (CATEGORICAL_CONDITION_FIELDS.has(field)) {
    return AUTOPILOT_OPERATORS.filter((operator) => operator.value === 'eq' || operator.value === 'neq');
  }
  return AUTOPILOT_OPERATORS.filter((operator) => operator.value !== 'in' && operator.value !== 'not_in');
}

interface RuleDraft {
  id: string | null;
  name: string;
  priority: number;
  isActive: boolean;
  triggerEvent: string;
  conditions: AutopilotCondition[];
  actions: AutopilotAction[];
}

const ACTION_DESCRIPTIONS: Record<AutopilotActionType, string> = {
  AUTO_APPROVE: 'Approve the return automatically',
  APPROVE: 'Approve the return',
  REJECT: 'Reject the return',
  FLAG_FOR_QC: 'Skip auto-refund; route to the QC queue',
  SCHEDULE_PICKUP: 'Book a reverse pickup with the courier',
  ISSUE_REFUND: 'Issue the refund to the customer',
  APPLY_RESTOCKING_FEE: 'Deduct the configured restocking fee',
  SEND_NOTIFICATION: 'Notify the customer or a team',
  ADD_TO_BLOCKLIST: 'Add the customer to the blocklist',
};

function ruleToDraft(rule: AutopilotRule): RuleDraft {
  return {
    id: rule.id,
    name: rule.name,
    priority: rule.priority,
    isActive: rule.isActive,
    triggerEvent: rule.triggerEvent,
    conditions:
      rule.conditions.length > 0 ? rule.conditions : [{ field: 'itemSubtotal', operator: 'lte', value: '' }],
    actions: rule.actions.length > 0 ? rule.actions : [{ type: 'AUTO_APPROVE' }],
  };
}

function emptyDraft(priority: number): RuleDraft {
  return {
    id: null,
    name: '',
    priority,
    isActive: true,
    triggerEvent: AUTOPILOT_TRIGGER_EVENTS[0],
    conditions: [{ field: 'itemSubtotal', operator: 'lte', value: '' }],
    actions: [{ type: 'AUTO_APPROVE' }],
  };
}

export function AutopilotScreen() {
  const { data, errorMessage, isLoading, reload } = useAsyncData<AutopilotRule[] | null>(
    (signal) => fetchAutopilotRules(signal),
    [],
  );

  const [order, setOrder] = useState<AutopilotRule[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [auditPage, setAuditPage] = useState(0);

  const { data: audit, reload: reloadAudit } = useAsyncData<Page<AutopilotAuditEntry> | null>(
    (signal) => fetchAutopilotAudit(auditPage, 20, signal),
    [auditPage],
  );

  const { data: settingsData } = useAsyncData<MerchantSettings | null>((signal) => fetchSettings(signal), []);
  const { data: reasonsData } = useAsyncData<ReturnReason[] | null>((signal) => fetchReasons(signal), []);

  const availableRefundModes = useMemo<SelectOption[]>(() => {
    const settings = resolveSettings(settingsData);
    const modes: SelectOption[] = [];
    if (settings.refundModeOriginal) {
      modes.push({ value: 'ORIGINAL_PAYMENT', label: refundModeLabel('ORIGINAL_PAYMENT') });
    }
    if (settings.refundModeStoreCredit) {
      modes.push({ value: 'STORE_CREDIT', label: refundModeLabel('STORE_CREDIT') });
    }
    if (settings.refundModeGiftCard) {
      modes.push({ value: 'GIFT_CARD', label: refundModeLabel('GIFT_CARD') });
    }
    if (settings.refundModeDiscountCode) {
      modes.push({ value: 'DISCOUNT_CODE', label: refundModeLabel('DISCOUNT_CODE') });
    }
    return modes;
  }, [settingsData]);

  const availableReasons = useMemo<SelectOption[]>(
    () =>
      resolveReturnReasons(reasonsData)
        .filter((reason) => reason.isActive)
        .map((reason) => ({ value: reason.slug, label: reason.label })),
    [reasonsData],
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    const sorted = [...data].sort((first, second) => first.priority - second.priority);
    setOrder(sorted);
    setSelectedId((current) => (current && sorted.some((rule) => rule.id === current) ? current : null));
  }, [data]);

  useEffect(() => {
    if (draft && draft.id === null) {
      return; // keep an in-progress new rule
    }
    const selected = order.find((rule) => rule.id === selectedId);
    setDraft(selected ? ruleToDraft(selected) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, order]);

  function openCreate() {
    setSelectedId(null);
    setDraft(emptyDraft(order.length + 1));
  }

  function closeEditor() {
    setSelectedId(null);
    setDraft(null);
  }

  async function toggleActive(rule: AutopilotRule) {
    try {
      await updateAutopilotRule(rule.id, { isActive: !rule.isActive });
      toast.success(rule.isActive ? 'Rule paused' : 'Rule activated');
      reload();
      reloadAudit();
    } catch (error) {
      toast.error(errorMessageOf(error));
    }
  }

  async function handleDrop(targetId: string) {
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      return;
    }
    const fromIndex = order.findIndex((rule) => rule.id === draggingId);
    const toIndex = order.findIndex((rule) => rule.id === targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggingId(null);
      return;
    }
    const next = [...order];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const priorityBefore = new Map(order.map((rule) => [rule.id, rule.priority]));
    const reprioritised = next.map((rule, index) => ({ ...rule, priority: index + 1 }));
    setOrder(reprioritised);
    setDraggingId(null);
    try {
      await Promise.all(
        reprioritised
          .filter((rule) => priorityBefore.get(rule.id) !== rule.priority)
          .map((rule) => updateAutopilotRule(rule.id, { priority: rule.priority })),
      );
      toast.success('Priority updated');
      reload();
      reloadAudit();
    } catch (error) {
      toast.error(errorMessageOf(error));
      reload();
      reloadAudit();
    }
  }

  async function handleSave() {
    if (!draft) {
      return;
    }
    const payload: AutopilotRuleInput = {
      name: draft.name.trim(),
      isActive: draft.isActive,
      priority: draft.priority,
      triggerEvent: draft.triggerEvent,
      conditions: draft.conditions.filter((condition) => condition.value.trim() !== ''),
      actions: draft.actions,
    };
    setIsBusy(true);
    try {
      if (draft.id) {
        await updateAutopilotRule(draft.id, payload);
      } else {
        await createAutopilotRule(payload);
      }
      toast.success('Rule saved');
      closeEditor();
      reload();
      reloadAudit();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function deleteRule(ruleId: string) {
    setIsBusy(true);
    try {
      await deleteAutopilotRule(ruleId);
      toast.success('Rule deleted');
      if (selectedId === ruleId || draft?.id === ruleId) {
        closeEditor();
      }
      reload();
      reloadAudit();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleDelete() {
    if (!draft?.id) {
      closeEditor();
      return;
    }
    await deleteRule(draft.id);
  }

  function handleDeleteFromList(rule: AutopilotRule) {
    void deleteRule(rule.id);
  }

  async function handleDuplicate() {
    if (!draft) {
      return;
    }
    setIsBusy(true);
    try {
      const created = await createAutopilotRule({
        name: `${draft.name.trim() || 'Rule'} (copy)`,
        isActive: false,
        priority: order.length + 1,
        triggerEvent: draft.triggerEvent,
        conditions: draft.conditions.filter((condition) => condition.value.trim() !== ''),
        actions: draft.actions,
      });
      toast.success('Rule duplicated');
      if (created?.id) {
        setSelectedId(created.id);
      }
      reload();
      reloadAudit();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  function handleCancel() {
    closeEditor();
  }

  const activeCount = order.filter((rule) => rule.isActive).length;
  const totalRuns = order.reduce((sum, rule) => sum + (rule.runCount ?? 0), 0);

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Auto-Pilot"
        description={`${order.length} ${order.length === 1 ? 'rule' : 'rules'}${
          totalRuns ? ` · ${totalRuns.toLocaleString()} runs to date` : ''
        }`}
        actions={<Button onClick={openCreate}>New rule</Button>}
      />

      {isLoading ? (
        <LoadingState message="Loading rules…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <KpiCard label="Active rules" value={`${activeCount}/${order.length}`} hint="running now" />
            <KpiCard label="Rule executions" value={totalRuns.toLocaleString()} hint="total runs to date" />
          </div>

          <RuleList
            rules={order}
            selectedId={draft?.id ?? selectedId}
            draggingId={draggingId}
            onSelect={(rule) => {
              setSelectedId(rule.id);
              setDraft(ruleToDraft(rule));
            }}
            onToggle={toggleActive}
            onDelete={handleDeleteFromList}
            onDragStart={setDraggingId}
            onDrop={handleDrop}
          />

          <RuleEditorDrawer isOpen={draft !== null} onClose={closeEditor}>
            {draft ? (
              <RuleEditor
                draft={draft}
               // rule={order.find((rule) => rule.id === draft.id)}
                availableRefundModes={availableRefundModes}
                availableReasons={availableReasons}
                isBusy={isBusy}
                onChange={setDraft}
                onSave={handleSave}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onCancel={handleCancel}
                onClose={closeEditor}
              />
            ) : null}
          </RuleEditorDrawer>

          <AuditLog page={audit} currentPage={auditPage} onPageChange={setAuditPage} />
        </div>
      )}
    </div>
  );
}

function RuleEditorDrawer({ isOpen, onClose, children }: { isOpen: boolean; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeydown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Rule editor">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-xl flex-col overflow-y-auto bg-surface shadow-pop animate-slide-in-right">
        {children}
      </div>
    </div>,
    document.body,
  );
}

function RuleList({
  rules,
  selectedId,
  draggingId,
  onSelect,
  onToggle,
  onDelete,
  onDragStart,
  onDrop,
}: {
  rules: AutopilotRule[];
  selectedId: string | null;
  draggingId: string | null;
  onSelect: (rule: AutopilotRule) => void;
  onToggle: (rule: AutopilotRule) => void;
  onDelete: (rule: AutopilotRule) => void;
  onDragStart: (id: string) => void;
  onDrop: (id: string) => void;
}) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-line px-5 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink">Rules · priority order</h2>
        <span className="text-xs text-ink-subtle">Drag to reorder</span>
      </div>
      {rules.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-ink-muted">No automation rules yet.</p>
      ) : (
        <ul className="divide-y divide-line">
          {rules.map((rule) => {
            const isSelected = rule.id === selectedId;
            return (
              <li
                key={rule.id}
                draggable
                onDragStart={() => onDragStart(rule.id)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => onDrop(rule.id)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 border-l-2 px-4 py-3.5 transition-colors',
                  isSelected ? 'border-brand bg-brand/5' : 'border-transparent hover:bg-surface-sunken/50',
                  draggingId === rule.id && 'opacity-50',
                )}
                onClick={() => onSelect(rule)}
              >
                <span
                  className="cursor-grab text-ink-subtle active:cursor-grabbing"
                  aria-label="Drag to reorder"
                  onClick={(event) => event.stopPropagation()}
                >
                  <GripIcon />
                </span>
                <span className="w-7 flex-shrink-0 text-sm font-semibold text-ink-subtle">#{rule.priority}</span>
                <div className="min-w-0 flex-1">
                  <div className={cn('truncate text-sm font-semibold', rule.isActive ? 'text-ink' : 'text-ink-subtle')}>
                    {rule.name || 'Untitled rule'}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-muted">
                    <span>
                      on <span className="font-medium">{humanizeEnum(rule.triggerEvent)}</span>
                    </span>
                    <span>{rule.runCount ?? 0} runs</span>
                    <span>Created {rule.createdAt ? formatDate(rule.createdAt) : '—'}</span>
                    <span>Updated {rule.updatedAt ? formatDate(rule.updatedAt) : '—'}</span>
                    <span>Last run {rule.lastRunAt ? formatDateTime(rule.lastRunAt) : 'never'}</span>
                  </div>
                </div>
                <div
                  className="flex flex-shrink-0 items-center gap-2"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Toggle checked={rule.isActive} onChange={() => onToggle(rule)} />
                  <button
                    type="button"
                    aria-label="Delete rule"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-danger-soft hover:text-danger"
                    onClick={() => onDelete(rule)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function BuilderBlock({
  tone,
  icon,
  kicker,
  title,
  children,
}: {
  tone: 'brand' | 'info' | 'success';
  icon: ReactNode;
  kicker: string;
  title: string;
  children: ReactNode;
}) {
  const toneClass =
    tone === 'brand' ? 'bg-brand text-white' : tone === 'info' ? 'bg-info text-white' : 'bg-success text-white';
  return (
    <div className="rounded-xl border border-line">
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', toneClass)}>{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{kicker}</span>
        <span className="text-sm font-semibold text-ink">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function RuleEditor({
  draft,
 // rule,
  availableRefundModes,
  availableReasons,
  isBusy,
  onChange,
  onSave,
  onDelete,
  onDuplicate,
  onCancel,
  onClose,
}: {
  draft: RuleDraft;
 // rule?: AutopilotRule;
  availableRefundModes: SelectOption[];
  availableReasons: SelectOption[];
  isBusy: boolean;
  onChange: (draft: RuleDraft) => void;
  onSave: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const triggerOptions = Array.from(new Set([...AUTOPILOT_TRIGGER_EVENTS, draft.triggerEvent])).map((value) => ({
    value,
    label: humanizeEnum(value),
  }));

  function updateCondition(index: number, patch: Partial<AutopilotCondition>) {
    onChange({
      ...draft,
      conditions: draft.conditions.map((condition, currentIndex) =>
        currentIndex === index ? { ...condition, ...patch } : condition,
      ),
    });
  }

  function updateConditionField(index: number, field: string) {
    const operators = operatorsForField(field);
    const currentOperator = draft.conditions[index]?.operator;
    updateCondition(index, {
      field,
      operator: operators.some((operator) => operator.value === currentOperator) ? currentOperator : operators[0].value,
      value: defaultValueForField(field, availableRefundModes, availableReasons),
    });
  }

  function updateActionType(index: number, type: AutopilotActionType) {
    onChange({
      ...draft,
      actions: draft.actions.map((action, currentIndex) =>
        currentIndex === index ? { ...action, type } : action,
      ),
    });
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <h2 className="truncate text-sm font-semibold uppercase tracking-wide text-ink">
          {draft.name.trim() || (draft.id ? 'Untitled rule' : 'New rule')}
        </h2>
        <div className="flex items-center gap-2">
          {draft.id ? (
            <Button size="sm" variant="secondary" onClick={onDuplicate} disabled={isBusy}>
              Duplicate
            </Button>
          ) : null}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-4 p-5">
        <TextField
          label="Rule name"
          value={draft.name}
          placeholder="e.g. Defective → flag for QC"
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />

        <BuilderBlock tone="brand" icon={<LightningIcon />} kicker="When" title="Trigger event">
          <SelectField
            className="w-full"
            value={draft.triggerEvent}
            onChange={(event) => onChange({ ...draft, triggerEvent: event.target.value })}
            options={triggerOptions}
          />
        </BuilderBlock>

        <BuilderBlock tone="info" icon={<FilterIcon />} kicker="If" title="Conditions">
          <div className="flex flex-col gap-3">
            {draft.conditions.map((condition, index) => (
              <div key={index}>
                {index > 0 ? (
                  <div className="mb-3 flex justify-center">
                    <span className="rounded-full bg-info-soft px-3 py-0.5 text-xs font-semibold text-info">AND</span>
                  </div>
                ) : null}
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
                  <SelectField
                    value={condition.field}
                    onChange={(event) => updateConditionField(index, event.target.value)}
                    options={AUTOPILOT_CONDITION_FIELDS}
                  />
                  <SelectField
                    value={condition.operator}
                    onChange={(event) => updateCondition(index, { operator: event.target.value as AutopilotOperator })}
                    options={operatorsForField(condition.field)}
                  />
                  {condition.field === 'refundMode' ? (
                    <SelectField
                      value={condition.value}
                      onChange={(event) => updateCondition(index, { value: event.target.value })}
                      options={availableRefundModes}
                      placeholder={availableRefundModes.length === 0 ? 'No refund modes enabled' : undefined}
                    />
                  ) : condition.field === 'reasonSlug' ? (
                    <SelectField
                      value={condition.value}
                      onChange={(event) => updateCondition(index, { value: event.target.value })}
                      options={availableReasons}
                      placeholder={availableReasons.length === 0 ? 'No reasons configured' : undefined}
                    />
                  ) : NUMERIC_CONDITION_FIELDS.has(condition.field) ? (
                    <TextField
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={condition.value}
                      placeholder="0"
                      onChange={(event) => updateCondition(index, { value: sanitizeAmountInput(event.target.value) })}
                    />
                  ) : POSITIVE_INT_CONDITION_FIELDS.has(condition.field) ? (
                    <TextField
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step="1"
                      value={condition.value}
                      placeholder="1"
                      onChange={(event) => updateCondition(index, { value: sanitizeCountInput(event.target.value) })}
                    />
                  ) : PINCODE_CONDITION_FIELDS.has(condition.field) ? (
                    <TextField
                      inputMode="numeric"
                      value={condition.value}
                      placeholder="e.g. 110001,400001"
                      onChange={(event) => updateCondition(index, { value: sanitizePincodeListInput(event.target.value) })}
                    />
                  ) : (
                    <TextField
                      value={condition.value}
                      placeholder="value"
                      onChange={(event) => updateCondition(index, { value: event.target.value })}
                    />
                  )}
                  <button
                    type="button"
                    aria-label="Remove condition"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-danger"
                    onClick={() =>
                      onChange({
                        ...draft,
                        conditions: draft.conditions.filter((_, currentIndex) => currentIndex !== index),
                      })
                    }
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="rounded-lg border border-dashed border-line py-2 text-sm font-medium text-ink-muted transition-colors hover:border-brand hover:text-brand"
              onClick={() =>
                onChange({
                  ...draft,
                  conditions: [...draft.conditions, { field: 'itemSubtotal', operator: 'lte', value: '' }],
                })
              }
            >
              + Add condition
            </button>
          </div>
        </BuilderBlock>

        <BuilderBlock tone="success" icon={<CheckIcon />} kicker="Then" title="Actions · run in order">
          <div className="flex flex-col gap-3">
            {draft.actions.map((action, index) => (
              <div key={index} className="rounded-lg border border-line p-3">
                <div className="flex items-start gap-3">
                  <span className="w-5 flex-shrink-0 pt-0.5 text-sm font-semibold text-ink-subtle">{index + 1}</span>
                  <div className="min-w-0 flex-1">
                    <SelectField
                      value={action.type}
                      onChange={(event) => updateActionType(index, event.target.value as AutopilotActionType)}
                      options={AUTOPILOT_ACTION_TYPES}
                    />
                    <p className="mt-1.5 text-xs text-ink-muted">{ACTION_DESCRIPTIONS[action.type]}</p>
                  </div>
                  <button
                    type="button"
                    aria-label="Remove action"
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-danger"
                    onClick={() =>
                      onChange({
                        ...draft,
                        actions: draft.actions.filter((_, currentIndex) => currentIndex !== index),
                      })
                    }
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="rounded-lg border border-dashed border-line py-2 text-sm font-medium text-ink-muted transition-colors hover:border-brand hover:text-brand"
              onClick={() => onChange({ ...draft, actions: [...draft.actions, { type: 'AUTO_APPROVE' }] })}
            >
              + Add action
            </button>
          </div>
        </BuilderBlock>

        <div className="rounded-xl bg-surface-sunken/60 px-4 py-3">
          <Toggle
            label="Active"
            description="Rule will fire on every matching event."
            checked={draft.isActive}
            onChange={(checked) => onChange({ ...draft, isActive: checked })}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-4">
        {draft.id ? (
          <Button variant="danger" onClick={onDelete} disabled={isBusy}>
            Delete
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isBusy}>
            Cancel
          </Button>
          <Button onClick={onSave} isLoading={isBusy} disabled={!draft.name.trim()}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Audit log ────────────────────────────────────────────────────────────────

const AUDIT_ACTION_META: Record<string, { label: string; tone: 'success' | 'pending' | 'danger' | 'neutral' }> = {
  CREATED: { label: 'Created', tone: 'success' },
  UPDATED: { label: 'Updated', tone: 'pending' },
  DELETED: { label: 'Deleted', tone: 'danger' },
};

function timePart(value: string | number[]): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  if (Array.isArray(value)) {
    const [, , , hour = 0, minute = 0, second = 0] = value;
    return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
}

/** Turn a rule's change map into readable phrases like "Priority 1 → 2". */
function summariseChanges(entry: AutopilotAuditEntry): string[] {
  if (!entry.changes) {
    return [];
  }
  return Object.entries(entry.changes).map(([field, change]) => {
    switch (field) {
      case 'name':
        return `Renamed “${String(change.from)}” → “${String(change.to)}”`;
      case 'priority':
        return `Priority ${String(change.from)} → ${String(change.to)}`;
      case 'isActive':
        return change.to ? 'Activated' : 'Deactivated';
      case 'triggerEvent':
        return `Trigger ${humanizeEnum(String(change.from))} → ${humanizeEnum(String(change.to))}`;
      case 'conditions':
        return `Conditions updated (${arrayLength(change.from)} → ${arrayLength(change.to)})`;
      case 'actions':
        return `Actions updated (${arrayLength(change.from)} → ${arrayLength(change.to)})`;
      default:
        return `${humanizeEnum(field)} changed`;
    }
  });
}

function arrayLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function AuditLog({
  page,
  currentPage,
  onPageChange,
}: {
  page: Page<AutopilotAuditEntry> | null | undefined;
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  const entries = page?.content ?? [];
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink">Audit log</h2>
        <p className="mt-0.5 text-xs text-ink-subtle">Every change made to your rules.</p>
      </div>

      {entries.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-ink-muted">No rule changes recorded yet.</p>
      ) : (
        <ul className="max-h-[360px] divide-y divide-line overflow-y-auto">
          {entries.map((entry) => {
            const meta = AUDIT_ACTION_META[entry.action] ?? { label: humanizeEnum(entry.action), tone: 'neutral' };
            const summary = summariseChanges(entry);
            return (
              <li key={entry.id} className="flex items-start gap-4 px-5 py-3.5">
                <div className="w-20 flex-shrink-0">
                  <div className="font-mono text-xs text-ink-subtle">{timePart(entry.createdAt)}</div>
                  <div className="font-mono text-[11px] text-ink-subtle/70">{formatDate(entry.createdAt)}</div>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">{entry.ruleName || 'Untitled rule'}</div>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {summary.length > 0 ? summary.join(' · ') : meta.label}
                    {entry.changedBy ? ` · by ${entry.changedBy}` : ''}
                  </p>
                </div>

                <Badge tone={meta.tone}>{meta.label}</Badge>
              </li>
            );
          })}
        </ul>
      )}

      {page && page.totalPages > 1 ? (
        <div className="border-t border-line px-5 py-3">
          <Pagination
            page={currentPage}
            totalPages={page.totalPages}
            totalElements={page.totalElements}
            onPageChange={onPageChange}
          />
        </div>
      ) : null}
    </Card>
  );
}
